import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IGroup } from '../../../models/groups.model';
import { doGetGroups } from '../../../store/group.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';
import { useSnackbar } from 'notistack';
import { Select, Table, TableColumnsType } from 'antd';

import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import { errorVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';
const { Option } = Select;

const SystemGroup: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // MB0001 = 그룹 권한 유형
  const MB0001 = commonCodes.find(item => item.commonCode === 'MB0001')?.detailCode;

  const { groups, isLoading } = useSelector((state: RootState) => state.group);
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { enqueueSnackbar } = useSnackbar();

  const [groupData, setGroupData] = useState<IGroup[]>([]);
  const [groupCount, setGroupCount] = useState(0);
  const [field, setField] = useState<any>();
  const [keyword, setKeyword] = useState('');

  const fieldList = [
    { label: t('system.group.column2'), value: 'korName' },
    { label: t('system.group.column3'), value: 'engName' }
  ];
  
  useEffect(() => {
    if (i18n.language === 'ko') {
      setField('korName');
    } else {
      setField('engName');
    }

    getGroups();
  }, []);

  useEffect(() => {
    // query to state
    if (query.field) {
      setField(String(query.field));
    }
    if (query.keyword) {
      setKeyword(String(query.keyword));
    }

    // data filtering
    if (groups) {
      let data: IGroup[] = groups;

      if (query.field && query.keyword) {
        data = data.filter(code => code[String(query.field)].indexOf(String(query.keyword)) !== -1);
      }

      setGroupCount(data.length);
      setGroupData(data);
    }
  }, [location, groups]);

  const getGroups = async () => {
    const res = await dispatch(doGetGroups());

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      case 404:
        enqueueSnackbar(t('common.message.notFound'), warningVariant);
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  };

  const onSearch = () => {
    if (keyword === '') {
      history.push('/system/group');
    } else if (groups && field) {
      const param = { field: field, keyword };
      history.push(`/system/group?${qs.stringify(param)}`);
    }
  };

  const columns: TableColumnsType<IGroup> = [
    {
      title: t('system.group.column1'),
      dataIndex: 'groupNo',
      key: 'groupNo',
      sorter: (a, b) => a.groupNo - b.groupNo
    },
    {
      title: t('system.group.column2'),
      dataIndex: 'korName',
      key: 'korName',
      sorter: (a, b) => a.korName?.localeCompare(b.korName)
    },
    {
      title: t('system.group.column3'),
      dataIndex: 'engName',
      key: 'engName',
      sorter: (a, b) => a.engName?.localeCompare(b.engName)
    },
    {
      title: t('system.group.column4'),
      dataIndex: 'authGroupDiv',
      key: 'authGroupDiv',
      sorter: (a, b) => a.authGroupDiv?.localeCompare(b.authGroupDiv),
      render: (authGroupDiv: string) => {
        const authGroup = MB0001?.find((item: any) => item.detailCode === authGroupDiv);
        return authGroup?.korName || authGroupDiv;
      }
    },
    {
      title: t('system.group.column5'),
      dataIndex: 'hqYn',
      key: 'hqYn',
      sorter: (a, b) => a.hqYn?.localeCompare(b.hqYn)
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-system-big.svg"
      parentMenu={t('common.menu.system')}
      currentMenu={t('system.group.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <Select
              value={field}
              onChange={value => setField(value)}
            >
              {
                fieldList?.map((item: any) => {
                return (
                  <Option
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </Option>
                )
                })
              }
              </Select>    
              <input
                type="text"
                className="form-control w-210p"
                value={keyword}
                onChange={(e => setKeyword(e.target.value))}
                onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
              />
              <div className="comm-btn__area">
                <Button
                  className='btn-basic small black'
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
          </li>
        </ul>
      </div>
    </div>    
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{groupCount}</b>{t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            <Button
              className='btn-basic pink'
              onClick={() => history.push('/system/group/create')}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={groupData || undefined}
            pagination={{ pageSize: 99999 }}
            onRow={record => {
              return {
                onClick: () => {
                  history.push(`/system/group/${record.groupNo}/edit`);
                }
              };
            }}
            rowClassName="pointer"
          />
        </div>
        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            <Button
              className='btn-basic pink'
              onClick={() => history.push('/system/group/create')}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
    </>
  )
}

export default SystemGroup;
